import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { analyticsAPI, khataAPI, posAPI } from '@/lib/api';
import WhatsAppComingSoonModal from '@/components/WhatsAppComingSoonModal';
import ReceiptModal from '@/components/ReceiptModal';
import SupplierPayables from './SupplierPayables';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  RefreshCw, Search,
  ChevronUp, ChevronDown, ChevronsUpDown, X,
  IndianRupee, CreditCard, BarChart2,
  AlertTriangle, MessageCircle, CheckCircle, Clock,
  ScrollText, Pencil, Trash2, Users, Building2, BookOpen,
} from 'lucide-react';

// ── Tier badge ────────────────────────────────────────────────────────────────
const TIERS = {
  champion: { label: 'Champion', emoji: '🏆', bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
  loyal:    { label: 'Loyal',    emoji: '⭐', bg: 'bg-blue-50',   text: 'text-blue-700',   border: 'border-blue-200'   },
  new:      { label: 'New',      emoji: '🆕', bg: 'bg-green-50',  text: 'text-green-700',  border: 'border-green-200'  },
  at_risk:  { label: 'At Risk',  emoji: '⚠️', bg: 'bg-amber-50',  text: 'text-amber-700',  border: 'border-amber-200'  },
};

const TierBadge = ({ tier }) => {
  const t = TIERS[tier] || TIERS.loyal;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${t.bg} ${t.text} ${t.border}`}>
      <span>{t.emoji}</span>{t.label}
    </span>
  );
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt    = (n) => typeof n === 'number' ? n.toLocaleString('en-IN') : '—';
const fmtCur = (n) => typeof n === 'number'
  ? `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
  : '—';

const SortIcon = ({ col, sortBy, sortDir }) => {
  if (sortBy !== col) return <ChevronsUpDown className="w-3 h-3 opacity-30 inline ml-1" />;
  return sortDir === 'asc'
    ? <ChevronUp   className="w-3 h-3 inline ml-1" />
    : <ChevronDown className="w-3 h-3 inline ml-1" />;
};

// ── Main component ────────────────────────────────────────────────────────────
const CreditCustomers = () => {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);

  const [showWaModal, setShowWaModal] = useState(false);

  // Khata collection
  const [collectModal, setCollectModal]     = useState({ open: false, customer: null });
  const [collectAmount, setCollectAmount]   = useState('');
  const [collectNote, setCollectNote]       = useState('');
  const [collectLoading, setCollectLoading] = useState(false);

  // Main tab: customers or suppliers
  const [mainTab, setMainTab] = useState('customers');

  // Credit view toggle
  const [creditView, setCreditView]           = useState('outstanding');
  const [collectionsLog, setCollectionsLog]   = useState(null);
  const [logLoading, setLogLoading]           = useState(false);
  const [creditSearch, setCreditSearch]       = useState('');
  const [creditSortBy, setCreditSortBy]       = useState('khata_balance');
  const [creditSortDir, setCreditSortDir]     = useState('desc');

  // Collection log actions
  const [logReceiptSale, setLogReceiptSale]       = useState(null);
  const [logDeleteTarget, setLogDeleteTarget]     = useState(null);
  const [logEditTarget, setLogEditTarget]         = useState(null);
  const [logEditNote, setLogEditNote]             = useState('');
  const [logEditAmount, setLogEditAmount]         = useState('');
  const [logActionLoading, setLogActionLoading]   = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await analyticsAPI.getCustomerInsights();
      setData(res.data);
    } catch {
      toast.error('Failed to load credit customers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCollect = (e, customer) => {
    e.stopPropagation();
    setCollectAmount(String((customer.khata_balance ?? 0).toFixed(2)));
    setCollectNote('');
    setCollectModal({ open: true, customer });
  };

  const closeCollect = () => {
    setCollectModal({ open: false, customer: null });
    setCollectAmount('');
    setCollectNote('');
  };

  const handleCollect = async () => {
    const amt = parseFloat(collectAmount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    setCollectLoading(true);
    try {
      const res = await khataAPI.collect({
        customer_id: collectModal.customer.customer_id,
        amount: amt,
        note: collectNote || undefined,
      });
      toast.success(`₹${res.data.collected.toLocaleString('en-IN')} collected from ${collectModal.customer.name}`);
      closeCollect();
      fetchData();
      setCollectionsLog(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to collect payment');
    } finally {
      setCollectLoading(false);
    }
  };

  const loadCollectionsLog = async () => {
    setLogLoading(true);
    try {
      const res = await khataAPI.getPayments();
      setCollectionsLog(res.data?.transactions ?? []);
    } catch {
      toast.error('Failed to load collections log');
    } finally {
      setLogLoading(false);
    }
  };

  const handleCreditView = (view) => {
    setCreditView(view);
    if (view === 'log') loadCollectionsLog();
  };

  const handleLogView = async (txn) => {
    if (!txn.bill_number) {
      toast.info('No bill linked to this collection');
      return;
    }
    try {
      const r = await posAPI.getSale(txn.bill_number);
      setLogReceiptSale(r.data);
    } catch {
      toast.error('Could not load receipt');
    }
  };

  const handleLogDelete = async () => {
    if (!logDeleteTarget) return;
    const targetEntry = collectionsLog?.find(t => t.id === logDeleteTarget);
    if (targetEntry?.bill_number || targetEntry?.note === 'Collected at POS') {
      toast.info(
        targetEntry.bill_number
          ? `Bill #${targetEntry.bill_number} was generated at POS. Go to Sales History to delete it.`
          : 'This collection was generated at POS. Go to Sales History to delete the bill.'
      );
      setLogDeleteTarget(null);
      return;
    }
    setLogActionLoading(true);
    try {
      await khataAPI.deletePayment(logDeleteTarget);
      toast.success('Collection undone');
      setLogDeleteTarget(null);
      setCollectionsLog(null);
      loadCollectionsLog();
    } catch {
      toast.error('Failed to undo collection');
    } finally {
      setLogActionLoading(false);
    }
  };

  const handleLogEditSave = async () => {
    if (!logEditTarget) return;
    const payload = { note: logEditNote };
    if (!logEditTarget.isPOS) {
      const newAmt = parseFloat(logEditAmount);
      if (isNaN(newAmt) || newAmt <= 0) { toast.error('Amount must be greater than 0'); return; }
      payload.amount = newAmt;
    }
    setLogActionLoading(true);
    try {
      await khataAPI.updatePayment(logEditTarget.id, payload);
      toast.success('Collection updated');
      setLogEditTarget(null);
      setCollectionsLog(null);
      loadCollectionsLog();
    } catch {
      toast.error('Failed to update collection');
    } finally {
      setLogActionLoading(false);
    }
  };

  const handleCreditSort = (col) => {
    setCreditSortDir(d => col === creditSortBy ? (d === 'asc' ? 'desc' : 'asc') : 'desc');
    setCreditSortBy(col);
  };

  const creditCustomers = useMemo(() => {
    if (!data?.customers) return [];
    let arr = [...data.customers].filter(c => (c.khata_balance ?? 0) > 0);
    if (creditSearch.trim()) {
      const q = creditSearch.toLowerCase();
      arr = arr.filter(c => c.name?.toLowerCase().includes(q) || c.mobile?.includes(q));
    }
    return arr.sort((a, b) => {
      const aRaw = a[creditSortBy];
      const bRaw = b[creditSortBy];
      if (typeof aRaw === 'number' || typeof bRaw === 'number') {
        const av = aRaw ?? (creditSortDir === 'asc' ? Infinity : -Infinity);
        const bv = bRaw ?? (creditSortDir === 'asc' ? Infinity : -Infinity);
        return creditSortDir === 'asc' ? av - bv : bv - av;
      }
      const av = (aRaw ?? '').toLowerCase();
      const bv = (bRaw ?? '').toLowerCase();
      return creditSortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [data, creditSearch, creditSortBy, creditSortDir]);

  const CreditSortTh = ({ col, label, align = 'left' }) => (
    <th
      onClick={() => handleCreditSort(col)}
      className={`text-${align} px-4 py-3 font-medium text-xs uppercase tracking-wide text-muted-foreground cursor-pointer select-none hover:bg-slate-100 transition-colors whitespace-nowrap`}
    >
      {label}<SortIcon col={col} sortBy={creditSortBy} sortDir={creditSortDir} />
    </th>
  );

  // Summary values
  const allCredit = data?.customers?.filter(c => (c.khata_balance ?? 0) > 0) ?? [];
  const totalOut  = allCredit.reduce((s, c) => s + (c.khata_balance ?? 0), 0);
  const avgOut    = allCredit.length > 0 ? totalOut / allCredit.length : 0;
  const maxOut    = allCredit.length > 0 ? Math.max(...allCredit.map(c => c.khata_balance ?? 0)) : 0;

  return (
    <>
    <div className="p-6 lg:p-8 space-y-5">

      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-primary" /> Khata
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Track customer credit and supplier invoice payments
          </p>
        </div>
        {mainTab === 'customers' && (
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} className="gap-1.5">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        )}
      </div>

      {/* Main tab toggle: Customers | Suppliers */}
      <div className="flex rounded-lg border overflow-hidden w-fit">
        <button
          onClick={() => setMainTab('customers')}
          className={`px-5 py-2.5 text-sm font-semibold flex items-center gap-2 transition-colors ${
            mainTab === 'customers' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'
          }`}
        >
          <Users className="w-4 h-4" /> Customers
        </button>
        <button
          onClick={() => setMainTab('suppliers')}
          className={`px-5 py-2.5 text-sm font-semibold flex items-center gap-2 transition-colors border-l ${
            mainTab === 'suppliers' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'
          }`}
        >
          <Building2 className="w-4 h-4" /> Suppliers
        </button>
      </div>

      {/* Suppliers tab content */}
      {mainTab === 'suppliers' && <SupplierPayables />}

      {/* Customers tab content */}
      {mainTab === 'customers' && <>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total Outstanding', value: fmtCur(totalOut),         icon: IndianRupee,   color: 'text-amber-600',   bg: 'bg-amber-50'   },
          { label: 'Credit Customers',  value: fmt(allCredit.length),     icon: CreditCard,    color: 'text-rose-600',    bg: 'bg-rose-50'    },
          { label: 'Avg Outstanding',   value: fmtCur(Math.round(avgOut)),icon: BarChart2,     color: 'text-purple-600',  bg: 'bg-purple-50'  },
          { label: 'Highest Balance',   value: fmtCur(maxOut),            icon: AlertTriangle, color: 'text-orange-600',  bg: 'bg-orange-50'  },
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <Card key={label} className="p-4">
            <div className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center mb-2`}>
              <Icon className={`w-4 h-4 ${color}`} />
            </div>
            <p className="text-xs text-muted-foreground leading-tight">{label}</p>
            <p className={`text-lg font-bold ${color} mt-0.5`}>{loading ? '…' : value}</p>
          </Card>
        ))}
      </div>

      {/* View toggle + search */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex rounded-lg border overflow-hidden">
          <button
            onClick={() => handleCreditView('outstanding')}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 transition-colors ${
              creditView === 'outstanding' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Clock className="w-3.5 h-3.5" /> Outstanding
          </button>
          <button
            onClick={() => handleCreditView('log')}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 transition-colors border-l ${
              creditView === 'log' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <CheckCircle className="w-3.5 h-3.5" /> Collections Log
          </button>
        </div>
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search name or mobile…"
            value={creditSearch}
            onChange={e => setCreditSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Outstanding view */}
      {creditView === 'outstanding' && (
        loading ? (
          <p className="text-center py-16 text-muted-foreground">Loading…</p>
        ) : creditCustomers.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-20" />
            <p className="font-medium">No outstanding credit balances</p>
            <p className="text-sm mt-1">All customers are cleared</p>
          </div>
        ) : (
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 border-b">
                <tr>
                  <CreditSortTh col="name"                      label="Customer" />
                  <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wide text-muted-foreground">Mobile</th>
                  <th className="text-center px-4 py-3 font-medium text-xs uppercase tracking-wide text-muted-foreground">Tier</th>
                  <CreditSortTh col="khata_balance"             label="Outstanding" align="right" />
                  <CreditSortTh col="days_since_last_purchase"  label="Last Visit"  align="center" />
                  <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wide text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {creditCustomers.map(c => (
                  <tr key={c.customer_id} className="hover:bg-amber-50/50 transition-colors">
                    <td className="px-4 py-3 font-medium text-slate-800">{c.name}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{c.mobile || '—'}</td>
                    <td className="px-4 py-3 text-center"><TierBadge tier={c.tier} /></td>
                    <td className="px-4 py-3 text-right font-bold text-amber-700">
                      ₹{(c.khata_balance ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </td>
                    <td className="px-4 py-3 text-center text-xs text-slate-500">
                      {c.days_since_last_purchase != null ? `${c.days_since_last_purchase}d ago` : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={(e) => openCollect(e, c)}
                          title="Collect payment"
                          className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors"
                        >
                          <IndianRupee className="w-3 h-3" /> Collect
                        </button>
                        <button
                          onClick={() => setShowWaModal(true)}
                          title="Send WhatsApp reminder"
                          className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100 transition-colors"
                        >
                          <MessageCircle className="w-3 h-3" /> Remind
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )
      )}

      {/* Collections Log view */}
      {creditView === 'log' && (
        logLoading ? (
          <p className="text-center py-16 text-muted-foreground">Loading…</p>
        ) : collectionsLog !== null && collectionsLog.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-20" />
            <p className="font-medium">No collections recorded yet</p>
            <p className="text-sm mt-1">Collect payments from the Outstanding tab</p>
          </div>
        ) : collectionsLog && (
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 border-b">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wide text-muted-foreground">Date</th>
                  <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wide text-muted-foreground">Customer</th>
                  <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wide text-muted-foreground">Mobile</th>
                  <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wide text-muted-foreground">Amount</th>
                  <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wide text-muted-foreground">Note</th>
                  <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wide text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {collectionsLog.map(t => (
                  <tr key={t.id} className="hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {t.created_at ? new Date(t.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-800">{t.customer_name || '—'}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{t.customer_mobile || '—'}</td>
                    <td className="px-4 py-3 text-right font-bold text-emerald-700">
                      ₹{(t.amount ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">{t.note || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleLogView(t)}
                          className="text-muted-foreground hover:text-primary"
                          title="View bill"
                        >
                          <ScrollText className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => {
                            const isPOS = t.note === 'Collected at POS' || !!t.bill_number;
                            if (isPOS) {
                              toast.info(t.bill_number
                                ? `Bill #${t.bill_number} was generated at POS. Go to Sales History to edit or delete it.`
                                : 'This collection was generated at POS. Go to Sales History to edit or delete the bill.');
                              return;
                            }
                            setLogEditTarget({ id: t.id, note: t.note || '', amount: t.amount ?? 0, isPOS: false });
                            setLogEditNote(t.note || '');
                            setLogEditAmount(String(t.amount ?? ''));
                          }}
                          className="text-muted-foreground hover:text-primary"
                          title="Edit collection"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => {
                            const isPOS = t.note === 'Collected at POS' || !!t.bill_number;
                            if (isPOS) {
                              toast.info(t.bill_number
                                ? `Bill #${t.bill_number} was generated at POS. Go to Sales History to delete it.`
                                : 'This collection was generated at POS. Go to Sales History to delete the bill.');
                              return;
                            }
                            setLogDeleteTarget(t.id);
                          }}
                          className="text-muted-foreground hover:text-red-500"
                          title="Undo collection"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-muted/20 border-t">
                <tr>
                  <td colSpan={3} className="px-4 py-2 text-xs text-slate-500">{collectionsLog.length} collections</td>
                  <td className="px-4 py-2 text-right text-xs font-bold text-emerald-700">
                    ₹{collectionsLog.reduce((s, t) => s + (t.amount ?? 0), 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </td>
                  <td />
                  <td />
                </tr>
              </tfoot>
            </table>
          </Card>
        )
      )}

      </> /* end customers tab */}
    </div>

    {showWaModal && <WhatsAppComingSoonModal onClose={() => setShowWaModal(false)} />}

    {/* Receipt modal for collection log */}
    {logReceiptSale && (
      <ReceiptModal sale={logReceiptSale} storeProfile={null} onClose={() => setLogReceiptSale(null)} />
    )}

    {/* Collect Payment modal */}
    {collectModal.open && collectModal.customer && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="bg-background rounded-xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
          <div className="p-5 border-b flex items-center justify-between">
            <div>
              <p className="font-bold text-lg">Collect Payment</p>
              <p className="text-sm text-muted-foreground">{collectModal.customer.name}</p>
            </div>
            <button onClick={closeCollect} className="p-1.5 rounded hover:bg-muted transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-5 space-y-4">
            <div className="flex items-center justify-between px-4 py-3 rounded-lg bg-amber-50 border border-amber-200">
              <span className="text-sm text-amber-700">Outstanding balance</span>
              <span className="font-bold text-amber-800">
                ₹{(collectModal.customer.khata_balance ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
              </span>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Amount to collect</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">₹</span>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  max={collectModal.customer.khata_balance ?? undefined}
                  className="pl-7"
                  value={collectAmount}
                  onChange={e => setCollectAmount(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleCollect()}
                  autoFocus
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Note <span className="text-muted-foreground font-normal">(optional)</span></label>
              <Input
                placeholder="e.g. Cash paid at counter"
                value={collectNote}
                onChange={e => setCollectNote(e.target.value)}
              />
            </div>
          </div>
          <div className="px-5 pb-5 flex gap-3">
            <Button variant="outline" className="flex-1" onClick={closeCollect} disabled={collectLoading}>
              Cancel
            </Button>
            <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={handleCollect} disabled={collectLoading}>
              {collectLoading ? 'Saving…' : 'Collect'}
            </Button>
          </div>
        </div>
      </div>
    )}

    {/* Delete confirmation modal */}
    {logDeleteTarget && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="bg-background rounded-xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
          <div className="p-5 border-b flex items-center justify-between">
            <p className="font-bold text-lg">Undo Collection?</p>
            <button onClick={() => setLogDeleteTarget(null)} className="p-1.5 rounded hover:bg-muted transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-5">
            <p className="text-sm text-muted-foreground">This will reverse the khata payment and restore the customer's outstanding balance.</p>
          </div>
          <div className="px-5 pb-5 flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => setLogDeleteTarget(null)} disabled={logActionLoading}>
              Cancel
            </Button>
            <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={handleLogDelete} disabled={logActionLoading}>
              {logActionLoading ? 'Undoing…' : 'Undo Collection'}
            </Button>
          </div>
        </div>
      </div>
    )}

    {/* Edit collection modal */}
    {logEditTarget && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="bg-background rounded-xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
          <div className="p-5 border-b flex items-center justify-between">
            <p className="font-bold text-lg">Edit Collection</p>
            <button onClick={() => setLogEditTarget(null)} className="p-1.5 rounded hover:bg-muted transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-5 space-y-4">
            {!logEditTarget.isPOS && (
              <div>
                <label className="block text-sm font-medium mb-1.5">Amount</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">₹</span>
                  <Input
                    type="number"
                    min="0.01"
                    step="0.01"
                    className="pl-7"
                    value={logEditAmount}
                    onChange={e => setLogEditAmount(e.target.value)}
                    autoFocus
                  />
                </div>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1.5">Note</label>
              <Input
                placeholder="e.g. Cash paid at counter"
                value={logEditNote}
                onChange={e => setLogEditNote(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleLogEditSave()}
                autoFocus={!!logEditTarget.isPOS}
              />
            </div>
          </div>
          <div className="px-5 pb-5 flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => setLogEditTarget(null)} disabled={logActionLoading}>
              Cancel
            </Button>
            <Button className="flex-1" onClick={handleLogEditSave} disabled={logActionLoading}>
              {logActionLoading ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default CreditCustomers;
